/* eslint-disable @typescript-eslint/no-explicit-any */
import { Key, useRef } from 'react';
import AlphabetListWithLetters, {
  AlphabetListWithLettersProps,
} from '@apps/components/AlphabetList';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import ViewMoreIcon from '@stack/assets/icons/car-brand-view-more.svg';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import isEmpty from 'lodash/isEmpty';
import Image from 'next/image';

import { Dialog, DialogRefProps } from '../Dialog';

export type BrandGroupProps<T = never> = {
  brands: T[];
  webView?: boolean;
  title?: string;
  isNewCar?: boolean;
  basePath: string;
  filterProps: AlphabetListWithLettersProps<T>;
};

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const NewCarBrandGroup = <T,>(props: BrandGroupProps<T>) => {
  const { brands, filterProps, webView, basePath } = props;

  // ======================= HOOKS
  const viewMoreDialogRef = useRef<DialogRefProps>(null);
  // const { t } = useTranslation('common');
  const { clickEvent } = usePostMsgContext();
  const { md } = useGetResponsive();

  // ======================= EVENTS
  const onClickViewMore = () => {
    viewMoreDialogRef.current?.open();
  };

  const onCloseViewMore = () => {
    viewMoreDialogRef.current?.close();
  };

  const renderBrandItem = (brand: {
    car_brand_id: any;
    car_brand_slug: any;
    car_brand_image: any;
    car_brand_name: any;
  }) => {
    const { car_brand_id, car_brand_slug, car_brand_image, car_brand_name } =
      brand;
    const isViewMore = car_brand_slug === 'view-more';
    const onClick = webView
      ? clickEvent(`${basePath}/${car_brand_slug}`, false, {
          title: car_brand_name,
          params: {
            brand: car_brand_slug,
          },
        })
      : () =>
          filterProps?.onItemSelect?.({
            slug: car_brand_slug,
            name: car_brand_name,
          });
    return (
      <Box
        key={car_brand_id}
        style={{
          width: '100%',
          height: '100%',
          minHeight: 60,
        }}
      >
        <Box
          onClick={isViewMore ? () => onClickViewMore() : onClick}
          sx={{
            textDecoration: 'none',
            position: 'relative',
            color: '#1E242F',
            cursor: 'pointer',
            '&:hover': {
              background: 'transparent',
              opacity: 0.8,
            },
          }}
        >
          <Stack direction={'column'} alignItems={'center'}>
            <Box
              sx={{
                width: '3.5rem',
                height: '3rem',
                aspectRatio: 1,
                padding: 2.5,
              }}
            >
              {isViewMore ? (
                <ViewMoreIcon />
              ) : (
                <Image
                  src={car_brand_image}
                  alt={car_brand_name}
                  width={46}
                  height={46}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              )}
            </Box>

            <p
              style={{
                fontSize: 10,
                lineHeight: '15.6px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '-0.25px',
                textDecoration: 'none',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                maxWidth: 70,
                textOverflow: 'ellipsis',
                ...(isViewMore && { color: '#31ACBF' }),
              }}
            >
              {car_brand_name}
            </p>
          </Stack>
        </Box>
      </Box>
    );
  };

  // ======================= VIEWS
  if (isEmpty(brands)) return null;
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '0.8rem', lg: '2rem' },
        }}
      >
        {/*<Box*/}
        {/*  sx={{*/}
        {/*    flexWrap: 'wrap',*/}
        {/*    display: 'flex',*/}
        {/*    justifyContent: { xs: 'center', md: 'flex-start' },*/}
        {/*    gap: { xs: '8px', md: '20px' },*/}
        {/*  }}*/}
        {/*>*/}
        <Grid container spacing={{ xs: '8px', md: '20px' }}>
          {brands?.map((brand: any, index: Key) => {
            if ((index as number) > (md ? 15 : 18)) return;
            return (
              <Grid key={`brand-${index}`} item xs={2.4} md={1.3}>
                {renderBrandItem(brand)}
              </Grid>
            );
          })}
        </Grid>
        {/*</Box>*/}
      </Box>
      <Dialog
        maxWidth="lg"
        title="Browse By Brand"
        enableCloseButton
        fullScreen
        showDivider
        titleProps={{
          textAlign: 'center',
          fontSize: 20,
          fontWeight: 700,
        }}
        closeButtonProps={{
          sx: {
            top: 16,
          },
        }}
        contentProps={{
          sx: {
            p: 0,
            overflowX: 'hidden',
            '::-webkit-scrollbar': {
              display: 'none',
            },
          },
        }}
        ref={viewMoreDialogRef}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AlphabetListWithLetters
            {...filterProps}
            containerProps={{
              sx: {
                backgroundColor: 'white',
                height: 'calc(100vh - 72px)',
                maxHeight: undefined,
              },
            }}
            onItemSelect={(item) => {
              filterProps?.onItemSelect?.(item);
              onCloseViewMore();
            }}
            fullWidth={true}
            webView={webView}
            basePath={basePath}
          />
        </Box>
      </Dialog>
    </>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default NewCarBrandGroup;
